import AppContext from "context/Context";
import { getColor, getItemFromStore } from "helpers/utils";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useReducer, useState } from "react";
import { settings } from "./config";
import useToggleStyle from "./hooks/useToggleStyle";
import { configReducer } from "./reducers/configReducer";

import { LoginAPI } from "api/LoginAPI";
import { Chart as ChartJS, registerables } from "chart.js";
import { AgencyDetailAPI, MenusAPI, UserProfileInfoAPI } from "api";

ChartJS.register(...registerables);

const initialState = {
  isLoading: false,
  user: JSON.parse(localStorage.getItem("user")) ?? null,
  tokenExpiry: null,
  token: localStorage.getItem("token"),
  agency: null,
  userProfile: null,
};

// console.log(initialState.user);

const Main = (props) => {
  const [state, setState] = useState(initialState);

  const fetchAgency = async (agencyId) => {
    if (!agencyId) return;

    try {
      const res = await AgencyDetailAPI.get({
        params: { id: agencyId },
      });

      setState((prev) => ({
        ...prev,
        agency: res?.data?.data || null,
      }));
    } catch (error) {
      console.error("Agency fetch error:", error);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const res = await UserProfileInfoAPI.get();

      setState((prev) => ({
        ...prev,
        userProfile: res?.data?.data || null,
      }));
    } catch (error) {
      console.error("User profile fetch error:", error);
    }
  };

  const refreshAgency = async () => {
    if (!state.user?.agencyId) return;
    await fetchAgency(state.user?.agencyId);
  };

  const login = async (loginId, password) => {
    const data = { loginId, password };

    const response = await LoginAPI.post(data);
    // const menus = await MenusAPI.get();

    const userInfo = response.data.userInfo;

    localStorage.setItem("token", response.data.token);
    localStorage.setItem("tokenExpiry", response.data.expiry);
    localStorage.setItem("user", JSON.stringify(userInfo));

    setState((prev) => ({
      ...prev,
      token: response.data.token,
      tokenExpiry: response.data.expiry,
      user: userInfo,
      isLoading: false,
    }));

    // ✅ fetch agency after login
    // if (userInfo?.agencyId) {
    //   fetchAgency(userInfo.agencyId);
    // }

    // ✅ after setting user
    if (userInfo?.type === "Admin" && userInfo?.agencyId) {
      fetchAgency(userInfo.agencyId);
    } else if (userInfo?.type === "USER") {
      fetchUserProfile();
    }

    const menus = await MenusAPI.get();

    const filterMenus = menus?.data?.data?.filter((d) => d.active);

    if (menus) {
      localStorage.setItem("menus", JSON.stringify(filterMenus));
    }

    window.dispatchEvent(new Event("storage")); // Notify PermissionProvider
    window.dispatchEvent(new Event("menusUpdated"));
  };

  // useEffect(() => {
  //   if (state.user?.agencyId) {
  //     fetchAgency(state.user.agencyId);
  //   }
  // }, [state.user]);

  useEffect(() => {
    if (!state.user) return;

    if (state.user.type === "Admin" && state.user?.agencyId) {
      fetchAgency(state.user.agencyId);
    } else if (state.user.type === "USER") {
      fetchUserProfile();
    }
  }, [state.user]);

  const logout = async () => {
    localStorage.clear();
    setState((prev) => ({
      ...prev,
      user: null,
      token: null,
      tokenExpiry: null,
      agency: null, // ✅ clear
      menus: null,
      userProfile: null,
    }));
  };

  useEffect(() => {
    const fetchMenus = async () => {
      const token = localStorage.getItem("token");
      const user = localStorage.getItem("user");
      const menus = localStorage.getItem("menus");

      if (token && user && (menus === null || menus === undefined)) {
        try {
          const menusFetch = await MenusAPI.get();
          localStorage.setItem("menus", JSON.stringify(menusFetch?.data?.data));
        } catch (error) {
          console.error("Error fetching menus:", error);
        }
      }
    };

    fetchMenus();
  }, []);

  const configState = {
    isFluid: getItemFromStore("isFluid", settings.isFluid),
    isRTL: getItemFromStore("isRTL", settings.isRTL),
    isDark: getItemFromStore("isDark", settings.isDark),
    theme: getItemFromStore("theme", settings.theme),
    navbarPosition: getItemFromStore("navbarPosition", settings.navbarPosition),
    disabledNavbarPosition: [],
    isNavbarVerticalCollapsed: getItemFromStore(
      "isNavbarVerticalCollapsed",
      settings.isNavbarVerticalCollapsed,
    ),
    navbarStyle: getItemFromStore("navbarStyle", settings.navbarStyle),
    currency: settings.currency,
    showBurgerMenu: settings.showBurgerMenu,
    showSettingPanel: false,
    navbarCollapsed: false,
    openAuthModal: false,
  };

  const [config, configDispatch] = useReducer(configReducer, configState);

  const setConfig = (key, value) => {
    configDispatch({
      type: "SET_CONFIG",
      payload: {
        key,
        value,
        setInStore: [
          "isFluid",
          "isRTL",
          "isDark",
          "theme",
          "navbarPosition",
          "isNavbarVerticalCollapsed",
          "navbarStyle",
        ].includes(key),
      },
    });
  };
  const { isLoaded } = useToggleStyle(config.isRTL, config.isDark);

  useEffect(() => {
    const isDark =
      config.theme === "auto"
        ? window.matchMedia("(prefers-color-scheme: dark)").matches
        : config.theme === "dark";

    setConfig("isDark", isDark);
  }, [config.theme]);

  const changeTheme = (theme) => {
    const isDark =
      theme === "auto"
        ? window.matchMedia("(prefers-color-scheme: dark)").matches
        : theme === "dark";

    document.documentElement.setAttribute(
      "data-bs-theme",
      isDark ? "dark" : "light",
    );

    setConfig("theme", theme);
    setConfig("isDark", isDark);
  };

  if (!isLoaded) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          backgroundColor: config.isDark ? getColor("dark") : getColor("light"),
        }}
      />
    );
  }

  // const values = useMemo(
  //   () => ({
  //     ...state,
  //     login,
  //     config,
  //     setConfig,
  //     configDispatch,
  //     changeTheme,
  //   }),
  //   [state]
  // );

  return (
    <AppContext.Provider
      value={{
        config,
        setConfig,
        configDispatch,
        changeTheme,
        login,
        logout,
        state, // contains user + agency
        agency: state.agency, // optional shortcut
        user: state.user, // optional shortcut
        userProfile: state.userProfile,
        refreshAgency: () => refreshAgency(),
      }}
    >
      {props.children}
    </AppContext.Provider>
  );
};

Main.propTypes = { children: PropTypes.node };

export const useAppContext = () => useContext(AppContext);
export default Main;
